import React from 'react';
import type {
    ChatActivity,
    ChatDoc,
    ChatFeed,
    ChatMessage,
    FileUploadProgress,
    SendResult
} from '../types/chat';
import type { ChatService, ChatType } from '../services/chatService';
import type { RichEditorImageUploadHandler } from '../../rich-editor/types';
import { collectInlineImageSrcs } from '../utils/inlineImages';

const POLL_INTERVAL_MS = 30_000;

export interface PatientChatContextValue {
    patientId: string;
    /** Which thread this provider serves, so every surface inside it can style itself for it. */
    chatType: ChatType;
    /** UI open/closed lifecycle. Purely presentational — it does not gate polling. */
    isOpen: boolean;
    open: () => void;
    close: () => void;
    toggle: () => void;
    loading: boolean;
    error: string | null;
    feed: ChatFeed | null;
    messages: ChatMessage[];
    activities: ChatActivity[];
    activitiesEnabled: boolean;
    /** False for the ODO-internal thread — seen receipts need an opposite
     *  side, which only the external, patient-facing conversation has. */
    receiptsEnabled: boolean;
    otherSideLastSeenAt: string | null;
    refresh: () => Promise<void>;
    refreshActivities: () => Promise<void>;
    send: (text: string, files: File[], doc?: ChatDoc) => Promise<SendResult>;
    edit: (
        messageId: string,
        newText: string,
        files?: File[],
        doc?: ChatDoc
    ) => Promise<SendResult>;
    /** Undefined when the injected service cannot delete messages. */
    remove?: (messageId: string) => Promise<void>;
    /** Undefined when the injected service cannot delete attachments. */
    removeAttachment?: (messageId: string, patientFileId: string) => Promise<void>;
    /** Undefined when the injected service cannot embed images in a body. */
    uploadInlineImage?: RichEditorImageUploadHandler;
    fileUploadProgress: FileUploadProgress[];
}

const PatientChatContext = React.createContext<PatientChatContextValue | undefined>(undefined);

export interface PatientChatProviderProps {
    patientId: string;
    service: ChatService;
    chatType?: ChatType;
    includeActivities?: boolean;
    children: React.ReactNode;
}

export const PatientChatProvider: React.FC<PatientChatProviderProps> = ({
    patientId,
    service,
    chatType,
    includeActivities,
    children
}) => {
    const [isOpen, setIsOpen] = React.useState(false);
    const [loading, setLoading] = React.useState(false);
    const [error, setError] = React.useState<string | null>(null);
    const [feed, setFeed] = React.useState<ChatFeed | null>(null);
    const [activities, setActivities] = React.useState<ChatActivity[]>([]);
    const [fileUploadProgress, setFileUploadProgress] = React.useState<FileUploadProgress[]>([]);
    const [otherSideLastSeenAt, setOtherSideLastSeenAt] = React.useState<string | null>(null);

    // Only the external thread has an opposite side to report a receipt for;
    // internal is one shared ODO row, so "seen" there would be self-referential.
    const isExternalChat = chatType !== 'internal';
    const activitiesEnabled = !!includeActivities && !!service.fetchActivities;

    // The service is an app-level singleton; keep it out of callback deps so
    // an inline object literal at the call site can't restart polling forever.
    const serviceRef = React.useRef(service);
    React.useEffect(() => {
        serviceRef.current = service;
    }, [service]);

    const refresh = React.useCallback(async (): Promise<void> => {
        const api = serviceRef.current;
        try {
            setLoading(true);
            setError(null);
            const [chatData, activityRows, status] = await Promise.all([
                api.fetchChat(patientId, chatType),
                activitiesEnabled && api.fetchActivities
                    ? api.fetchActivities(patientId, chatType)
                    : Promise.resolve([] as ChatActivity[]),
                api.fetchChatStatus(patientId, chatType)
            ]);
            setFeed(chatData);
            setActivities(activityRows);
            setOtherSideLastSeenAt(status?.otherSideLastSeenAt ?? null);
        } catch (e: unknown) {
            const err = e as { message?: string };
            setError(err?.message || 'Failed to load chat');
        } finally {
            setLoading(false);
        }
    }, [patientId, chatType, activitiesEnabled]);

    const refreshActivities = React.useCallback(async (): Promise<void> => {
        const api = serviceRef.current;
        if (!activitiesEnabled || !api.fetchActivities) return;
        try {
            const activityRows = await api.fetchActivities(patientId, chatType);
            setActivities(activityRows);
        } catch (err) {
            // The activity log is secondary to the user's primary action (mark-as-read,
            // assign, …) which already succeeded server-side, so we don't surface this
            // through the provider's `error` state.
            api.onError?.(err, { scope: 'refreshActivities', patientId });
        }
    }, [patientId, chatType, activitiesEnabled]);

    React.useEffect(() => {
        refresh();
    }, [refresh]);

    // The initial refresh() above is a one-shot fetch — without this, a checkmark
    // only flips from sent to seen (or a new incoming message appears) after the
    // user manually reloads the tab. Poll the cheap chat-status endpoint instead
    // of the full (Monday-backed) feed, and only pay for a full refresh() when the
    // status check says something actually changed.
    React.useEffect(() => {
        let cancelled = false;
        let timer: ReturnType<typeof setTimeout> | undefined;

        const tick = async (): Promise<void> => {
            if (cancelled || document.hidden) return;
            try {
                const status = await serviceRef.current.fetchChatStatus(patientId, chatType);
                if (cancelled) return;
                setOtherSideLastSeenAt(status.otherSideLastSeenAt);
                if (status.hasUpdate) {
                    await refresh();
                }
            } catch {
                // Poll failure is non-critical — the next tick (or a manual refresh)
                // will pick it back up.
            }
            if (!cancelled) {
                timer = setTimeout(tick, POLL_INTERVAL_MS);
            }
        };

        const handleVisibilityChange = (): void => {
            if (cancelled) return;
            if (timer) clearTimeout(timer);
            if (document.visibilityState === 'visible') tick();
        };

        timer = setTimeout(tick, POLL_INTERVAL_MS);
        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            cancelled = true;
            if (timer) clearTimeout(timer);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [patientId, chatType, refresh]);

    const open = React.useCallback(() => setIsOpen(true), []);
    const close = React.useCallback(() => setIsOpen(false), []);
    const toggle = React.useCallback(() => setIsOpen((v) => !v), []);

    const uploadFilesToUpdate = React.useCallback(
        async (files: File[], updateId: string | number): Promise<string[]> => {
            const api = serviceRef.current;
            const failedFiles: string[] = [];
            setFileUploadProgress(
                files.map((f) => ({ fileName: f.name, progress: 0, status: 'uploading' as const }))
            );

            const results = await Promise.allSettled(
                files.map((file, idx) =>
                    api
                        .uploadFile(
                            patientId,
                            file,
                            updateId,
                            (progress) => {
                                setFileUploadProgress((prev) =>
                                    prev.map((p, i) => (i === idx ? { ...p, progress } : p))
                                );
                            },
                            chatType
                        )
                        .then(() => {
                            setFileUploadProgress((prev) =>
                                prev.map((p, i) =>
                                    i === idx
                                        ? { ...p, progress: 100, status: 'success' as const }
                                        : p
                                )
                            );
                        })
                )
            );

            for (const [idx, uploadResult] of results.entries()) {
                if (uploadResult.status === 'rejected') {
                    failedFiles.push(files[idx].name);
                    setFileUploadProgress((prev) =>
                        prev.map((p, i) =>
                            i === idx
                                ? {
                                      ...p,
                                      status: 'failed' as const,
                                      error:
                                          (uploadResult.reason as Error)?.message ?? 'Upload failed'
                                  }
                                : p
                        )
                    );
                }
            }

            return failedFiles;
        },
        [patientId, chatType]
    );

    const send = React.useCallback(
        async (text: string, files: File[], doc?: ChatDoc): Promise<SendResult> => {
            let failedFiles: string[] = [];
            const sendResult = await serviceRef.current.sendMessage(patientId, text, chatType, doc);
            const updateId = sendResult?.updateId;

            if (files.length > 0 && updateId) {
                failedFiles = await uploadFilesToUpdate(files, updateId);
            }

            await refresh();

            if (failedFiles.length === 0) {
                setFileUploadProgress([]);
            }

            return { failedFiles };
        },
        [patientId, chatType, refresh, uploadFilesToUpdate]
    );

    // Mirror of `feed` for handlers that need the latest snapshot without
    // re-creating their memo on every poll tick. Synced in an effect rather
    // than during render so the write stays out of the render phase.
    const feedRef = React.useRef<ChatFeed | null>(null);
    React.useEffect(() => {
        feedRef.current = feed;
    }, [feed]);

    const edit = React.useCallback(
        async (
            messageId: string,
            newText: string,
            files: File[] = [],
            doc?: ChatDoc
        ): Promise<SendResult> => {
            const prevFeed = feedRef.current;
            setFeed((prev) => {
                if (!prev) return prev;
                return {
                    ...prev,
                    messages: prev.messages.map((m) =>
                        m.id === messageId
                            ? {
                                  ...m,
                                  htmlBody: newText,
                                  format: 'html' as const,
                                  ...(doc && m.bodyDoc && { bodyDoc: doc })
                              }
                            : m
                    ),
                    // Images added in the edit show their local preview until the refetch signs them.
                    fileUrls: { ...(doc && collectInlineImageSrcs(doc)), ...prev.fileUrls }
                };
            });

            let failedFiles: string[] = [];
            try {
                await serviceRef.current.editMessage(patientId, messageId, newText, doc);

                if (files.length > 0) {
                    // Passed through as-is. This used to be Number(messageId)
                    // guarded by !Number.isNaN, which was correct only while
                    // ids were Monday update ids: a v2 UUID makes it NaN, and
                    // the guard then skipped the upload with nothing logged and
                    // no error surfaced to the user.
                    failedFiles = await uploadFilesToUpdate(files, messageId);
                }
                await refresh();
            } catch (e) {
                setFeed(prevFeed);
                throw e;
            }

            if (failedFiles.length === 0) {
                setFileUploadProgress([]);
            }

            return { failedFiles };
        },
        [patientId, refresh, uploadFilesToUpdate]
    );

    const canRemove = !!service.deleteMessage;
    const remove = React.useCallback(
        async (messageId: string): Promise<void> => {
            const api = serviceRef.current;
            if (!api.deleteMessage) return;
            const prevFeed = feedRef.current;
            setFeed((prev) => {
                if (!prev) return prev;
                return { ...prev, messages: prev.messages.filter((m) => m.id !== messageId) };
            });

            try {
                await api.deleteMessage(patientId, messageId);
                await refresh();
            } catch (e) {
                setFeed(prevFeed);
                throw e;
            }
        },
        [patientId, refresh]
    );

    const canRemoveAttachment = !!service.deleteMessageFile;
    const removeAttachment = React.useCallback(
        async (messageId: string, patientFileId: string): Promise<void> => {
            const api = serviceRef.current;
            if (!api.deleteMessageFile) return;
            const prevFeed = feedRef.current;
            setFeed((prev) => {
                if (!prev) return prev;
                return {
                    ...prev,
                    messages: prev.messages.map((m) =>
                        m.id === messageId
                            ? {
                                  ...m,
                                  assets: m.assets.filter((a) => a.patientFileId !== patientFileId)
                              }
                            : m
                    )
                };
            });

            try {
                await api.deleteMessageFile(patientId, messageId, patientFileId);
                await refresh();
            } catch (e) {
                setFeed(prevFeed);
                throw e;
            }
        },
        [patientId, refresh]
    );

    const canUploadInline = !!service.uploadInlineImage;
    const uploadInlineImage = React.useCallback(
        (file: File) => {
            const api = serviceRef.current;
            if (!api.uploadInlineImage) {
                return Promise.reject(new Error('Chat service cannot embed images.'));
            }
            return api.uploadInlineImage(patientId, file, chatType);
        },
        [patientId, chatType]
    );

    const value = React.useMemo<PatientChatContextValue>(
        () => ({
            patientId,
            chatType: chatType ?? 'external',
            isOpen,
            open,
            close,
            toggle,
            loading,
            error,
            feed,
            messages: feed?.messages ?? [],
            activities,
            activitiesEnabled,
            receiptsEnabled: isExternalChat,
            otherSideLastSeenAt,
            refresh,
            refreshActivities,
            fileUploadProgress,
            send,
            edit,
            remove: canRemove ? remove : undefined,
            removeAttachment: canRemoveAttachment ? removeAttachment : undefined,
            uploadInlineImage: canUploadInline ? uploadInlineImage : undefined
        }),
        [
            patientId,
            chatType,
            isOpen,
            open,
            close,
            toggle,
            loading,
            error,
            feed,
            activities,
            activitiesEnabled,
            isExternalChat,
            otherSideLastSeenAt,
            refresh,
            refreshActivities,
            fileUploadProgress,
            send,
            edit,
            canRemove,
            remove,
            canRemoveAttachment,
            removeAttachment,
            canUploadInline,
            uploadInlineImage
        ]
    );

    return <PatientChatContext.Provider value={value}>{children}</PatientChatContext.Provider>;
};

export const usePatientChat = (): PatientChatContextValue => {
    const ctx = React.useContext(PatientChatContext);
    if (!ctx) throw new Error('usePatientChat must be used within PatientChatProvider');
    return ctx;
};

export const usePatientChatOptional = (): PatientChatContextValue | undefined => {
    return React.useContext(PatientChatContext);
};
